/**
 * GA4 + Meta Pixel dataLayer helpers.
 * Do NOT call fbq('init') or load fbevents.js here — Pixel init stays in GTM-TRHRLXPH.
 * metaBridge.js may call fbq('track', …, {eventID}) after GTM loads the pixel.
 */

export const CURRENCY = "AMD";
export const META_PIXEL_ID = "958443791456238";

/** Temporary production debug — remove after GA4 QA. */
const DEBUG_DATALAYER = true;

const PURCHASE_STORAGE_KEY = "vlv_ga4_purchase_ids";
const PURCHASE_STORAGE_KEY_LS = "vlv_ga4_purchase_ids_ls";
const EVENT_ID_EVENTS = new Set([
  "view_item",
  "search",
  "add_to_cart",
  "add_to_wishlist",
  "begin_checkout",
  "add_payment_info",
  "purchase",
  "generate_lead",
  "sign_up",
  "phone_click",
  "whatsapp_click",
  "viber_click",
  "quick_order",
  "credit_application",
  "view_cart",
]);

/** dataLayer event → Meta standard / custom event name */
export const META_EVENT_MAP = {
  view_item: "ViewContent",
  search: "Search",
  add_to_cart: "AddToCart",
  add_to_wishlist: "AddToWishlist",
  begin_checkout: "InitiateCheckout",
  add_payment_info: "AddPaymentInfo",
  purchase: "Purchase",
  generate_lead: "Lead",
  sign_up: "CompleteRegistration",
  phone_click: "Contact",
  whatsapp_click: "Contact",
  viber_click: "Contact",
  /** Custom Meta event (not a standard Pixel event) */
  view_cart: "ViewCart",
};

/** Meta events that must use fbq('trackCustom', …) instead of fbq('track', …). */
export const META_CUSTOM_EVENTS = new Set(["ViewCart"]);
/** In-memory dedupe for SPA navigation within the same page lifetime. */
const purchaseIdsMemory = new Set();

/** Keys that must never enter dataLayer (PII / secrets). */
const PII_KEYS = new Set([
  "email",
  "e_mail",
  "phone",
  "phone_number",
  "telephone",
  "tel",
  "mobile",
  "name",
  "first_name",
  "last_name",
  "surname",
  "firstname",
  "lastname",
  "full_name",
  "address",
  "map_address",
  "street",
  "city",
  "state",
  "zip",
  "postal",
  "password",
  "user_password",
  "password_confirmation",
  "cv",
  "resume",
  "file",
  "card",
  "card_number",
  "cvv",
  "cvc",
  "iban",
  "user_data",
  "em",
  "ph",
  "fn",
  "ln",
]);

function canUseDom() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

export function getDataLayer() {
  if (!canUseDom()) return null;
  // Never replace an existing array (GTM / prior pushes). Only init if missing/invalid.
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
  return window.dataLayer;
}

function debugDataLayerPush(event, payload) {
  if (!DEBUG_DATALAYER || !canUseDom()) return;
  try {
    // Temporary: visible in production DevTools while validating ecommerce.
    console.info("[dataLayer]", event, payload);
  } catch {
    /* ignore */
  }
}

export function toNumber(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value)
    .replace(/֏/g, "")
    .replace(/\s/g, "")
    .replace(/,/g, "")
    .trim();
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Unique event_id for Meta CAPI / Pixel dedupe.
 * Stable when `stableKey` is provided (e.g. order_number).
 */
export function createEventId(prefix, stableKey) {
  const p = String(prefix || "event").replace(/[^a-z0-9_]+/gi, "_");
  if (stableKey != null && String(stableKey).trim() !== "") {
    return `${p}_${String(stableKey).trim()}`;
  }
  const rand =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 16)
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  return `${p}_${rand}`;
}

const ALLOWED_NAME_KEYS = new Set([
  "item_name",
  "form_name",
  "item_list_name",
  "page_title",
  "payment_type",
  "lead_type",
  "credit_term",
  "social_network",
]);

export function isPiiKey(key) {
  if (!key) return false;
  const k = String(key).toLowerCase();
  if (ALLOWED_NAME_KEYS.has(k)) return false;
  if (PII_KEYS.has(k)) return true;
  if (k.includes("password") || k.includes("email") || k.includes("phone")) return true;
  // block personal *name fields, keep product/analytics *name keys
  if (
    (k === "name" || k.endsWith("_name") || k.startsWith("name_")) &&
    !k.startsWith("item_")
  ) {
    return true;
  }
  return false;
}

/** Deep-ish strip of PII keys from plain objects (not items ecommerce). */
export function stripPii(input, { allowItemFields = false } = {}) {
  if (input == null || typeof input !== "object") return input;
  if (Array.isArray(input)) {
    return input.map((v) => stripPii(v, { allowItemFields }));
  }
  const out = {};
  Object.keys(input).forEach((key) => {
    if (!allowItemFields && isPiiKey(key)) return;
    const v = input[key];
    if (v && typeof v === "object" && !Array.isArray(v) && key !== "items") {
      out[key] = stripPii(v, { allowItemFields });
    } else if (key === "items" && Array.isArray(v)) {
      out[key] = v.map((item) => stripPii(item, { allowItemFields: true }));
    } else {
      out[key] = v;
    }
  });
  return out;
}

function pruneEmpty(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const out = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v) && v.length === 0) return;
    out[k] = v;
  });
  return out;
}

/**
 * Build ecommerce payload with only applicable fields; numbers stay numbers.
 */
export function buildEcommerce({
  currency = CURRENCY,
  value,
  transaction_id,
  shipping,
  tax,
  coupon,
  payment_type,
  shipping_tier,
  item_list_id,
  item_list_name,
  items,
} = {}) {
  const ecommerce = pruneEmpty({
    currency: currency || CURRENCY,
    value: value != null ? toNumber(value) : undefined,
    transaction_id: transaction_id != null ? String(transaction_id) : undefined,
    shipping: shipping != null ? toNumber(shipping) : undefined,
    tax: tax != null ? toNumber(tax) : undefined,
    coupon: coupon ? String(coupon) : undefined,
    payment_type: payment_type ? String(payment_type) : undefined,
    shipping_tier: shipping_tier ? String(shipping_tier) : undefined,
    item_list_id: item_list_id != null ? String(item_list_id) : undefined,
    item_list_name: item_list_name != null ? String(item_list_name) : undefined,
    items: Array.isArray(items) ? items : undefined,
  });
  return ecommerce;
}

/**
 * Meta custom_data fields derived from GA4 ecommerce items.
 */
export function buildMetaProductParams(ecommerce = {}) {
  const items = Array.isArray(ecommerce.items) ? ecommerce.items : [];
  const content_ids = items
    .map((it) => (it?.item_id != null ? String(it.item_id) : null))
    .filter(Boolean);
  const contents = items
    .map((it) =>
      pruneEmpty({
        id: it?.item_id != null ? String(it.item_id) : undefined,
        quantity: Math.max(1, toNumber(it?.quantity) || 1),
        item_price: toNumber(it?.price),
      })
    )
    .filter((c) => c.id);

  return pruneEmpty({
    content_ids: content_ids.length ? content_ids : undefined,
    content_type: content_ids.length ? "product" : undefined,
    contents: contents.length ? contents : undefined,
    value:
      ecommerce.value != null && Number.isFinite(toNumber(ecommerce.value))
        ? toNumber(ecommerce.value)
        : undefined,
    currency: ecommerce.currency || CURRENCY,
    search_string: ecommerce.search_string || undefined,
  });
}

/**
 * Normalize any product-like object into a GA4 item.
 */
export function normalizeProduct(product, quantity = 1, listContext = {}) {
  if (!product || typeof product !== "object") return null;

  const nested = product.product && typeof product.product === "object" ? product.product : null;
  const pricing = product.pricing || nested?.pricing || {};
  const brandObj = product.brand || nested?.brand || {};
  const categories = product.categories || nested?.categories || [];
  const skus = product.skus || nested?.skus || [];
  const sku0 = skus[0] || {};

  const lang =
    (canUseDom() &&
      (document.documentElement?.lang || window.__VLV_LANG__ || "hy")) ||
    "hy";
  const catKey = `name_${lang}`;

  const category =
    product.item_category ||
    (typeof product.category === "string" ? product.category : null) ||
    product.category?.[catKey] ||
    product.category?.name ||
    categories[0]?.[catKey] ||
    categories[0]?.name ||
    categories[0]?.name_hy ||
    "";

  const category2 =
    product.item_category2 ||
    categories[1]?.[catKey] ||
    categories[1]?.name ||
    categories[1]?.name_hy ||
    "";

  const itemId = String(
    product.item_id ||
      product.li_product_id ||
      nested?.li_product_id ||
      product.seller_product_id ||
      product.seller_id ||
      product.id ||
      nested?.id ||
      product.product_id ||
      product.seller_product_sku_id ||
      product.seller_product_skus ||
      sku0.id ||
      ""
  );

  const brandName =
    (typeof brandObj === "string" ? brandObj : brandObj?.name) ||
    product.item_brand ||
    product.brandName ||
    "";

  const rawName =
    product.item_name ||
    product.productName ||
    product.product_name ||
    nested?.product_name ||
    product.title ||
    "";

  const variant =
    product.item_variant ||
    product.variant ||
    sku0.variant ||
    sku0.sku ||
    product.color ||
    "";

  const price = toNumber(
    product.price ??
      product.item_price ??
      product.promoPrice ??
      product.OnlineSelingPrice ??
      product.sellingPrice ??
      pricing.promo_price ??
      (pricing.online_price && pricing.online_selling_price
        ? pricing.online_selling_price
        : null) ??
      pricing.selling_price ??
      product.promo_price ??
      (product.online_price && product.online_selling_price
        ? product.online_selling_price
        : null) ??
      nested?.promo_price ??
      (nested?.online_price && nested?.online_selling_price
        ? nested.online_selling_price
        : null) ??
      sku0.promo_price ??
      sku0.selling_price ??
      product.selling_price ??
      product.recommended_retail_price ??
      0
  );

  const qty = Math.max(1, toNumber(quantity) || 1);
  if (!itemId && !rawName) return null;

  const item = pruneEmpty({
    item_id: itemId || String(rawName),
    item_name: String(rawName || itemId),
    item_brand: brandName ? String(brandName) : undefined,
    item_category: category ? String(category) : undefined,
    item_category2: category2 ? String(category2) : undefined,
    item_variant: variant ? String(variant) : undefined,
    price,
    quantity: qty,
    item_list_id:
      listContext.item_list_id != null
        ? String(listContext.item_list_id)
        : product.item_list_id != null
          ? String(product.item_list_id)
          : undefined,
    item_list_name:
      listContext.item_list_name != null
        ? String(listContext.item_list_name)
        : product.item_list_name != null
          ? String(product.item_list_name)
          : undefined,
    index: listContext.index != null ? Number(listContext.index) : undefined,
  });

  return item;
}

export function normalizeProducts(products, listContext = {}) {
  if (!Array.isArray(products)) return [];
  return products
    .map((p, index) =>
      normalizeProduct(p, p?.qty || p?.quantity || p?.count || 1, {
        ...listContext,
        index: listContext.index != null ? listContext.index : index,
      })
    )
    .filter(Boolean);
}

export function cartItemsValue(items) {
  return (items || []).reduce(
    (sum, item) => sum + toNumber(item.price) * toNumber(item.quantity || 1),
    0
  );
}

function ensureItems(items, listContext) {
  if (!Array.isArray(items) || !items.length) return [];
  if (items[0]?.item_id != null && typeof items[0]?.price === "number") {
    return items.map((it) =>
      pruneEmpty({
        ...it,
        price: toNumber(it.price),
        quantity: Math.max(1, toNumber(it.quantity) || 1),
        item_id: String(it.item_id),
        item_name: it.item_name != null ? String(it.item_name) : undefined,
      })
    );
  }
  return normalizeProducts(items, listContext);
}

/**
 * Non-ecommerce event.
 */
export function pushEvent(name, parameters = {}, { eventId, eventIdKey } = {}) {
  const dl = getDataLayer();
  if (!dl || !name) return null;

  const params = stripPii(parameters || {});
  const payload = pruneEmpty({ event: name, ...params });

  if (EVENT_ID_EVENTS.has(name) || eventId || eventIdKey) {
    payload.event_id =
      eventId ||
      createEventId(name, eventIdKey != null ? eventIdKey : undefined);
  }

  // Final PII sweep on top-level keys
  Object.keys(payload).forEach((k) => {
    if (isPiiKey(k)) delete payload[k];
  });

  debugDataLayerPush(name, payload);
  dl.push(payload);
  return payload;
}

/**
 * Ecommerce event: always clear ecommerce first.
 */
export function pushEcommerceEvent(
  name,
  ecommerce = {},
  parameters = {},
  { eventId, eventIdKey } = {}
) {
  const dl = getDataLayer();
  if (!dl || !name) return null;

  const clearPayload = { ecommerce: null };
  debugDataLayerPush("ecommerce:null", clearPayload);
  dl.push(clearPayload);

  const cleanEcom = stripPii(pruneEmpty(ecommerce || {}), { allowItemFields: true });
  const params = stripPii(parameters || {});

  const metaParams = buildMetaProductParams(cleanEcom);
  const payload = pruneEmpty({
    event: name,
    ...params,
    ecommerce: cleanEcom,
    // Top-level Meta fields for GTM / metaBridge (dedupe via event_id).
    ...metaParams,
    meta_event: META_EVENT_MAP[name] || undefined,
  });

  if (EVENT_ID_EVENTS.has(name) || eventId || eventIdKey) {
    payload.event_id =
      eventId ||
      createEventId(name, eventIdKey != null ? eventIdKey : undefined);
  }

  Object.keys(payload).forEach((k) => {
    if (k !== "ecommerce" && isPiiKey(k)) delete payload[k];
  });

  debugDataLayerPush(name, payload);
  dl.push(payload);
  return payload;
}

export function trackViewItemList({ item_list_id, item_list_name, items }) {
  const normalized = ensureItems(items, { item_list_id, item_list_name });
  if (!normalized.length) return null;
  return pushEcommerceEvent(
    "view_item_list",
    buildEcommerce({
      item_list_id,
      item_list_name,
      items: normalized,
    })
  );
}

export function trackSelectItem({ item_list_id, item_list_name, item, product }) {
  const normalized =
    item?.item_id != null
      ? ensureItems([item], { item_list_id, item_list_name })[0]
      : normalizeProduct(product || item, 1, { item_list_id, item_list_name });
  if (!normalized) return null;
  return pushEcommerceEvent(
    "select_item",
    buildEcommerce({
      item_list_id,
      item_list_name,
      items: [normalized],
    })
  );
}

export function trackViewItem(product, quantity = 1) {
  const item = normalizeProduct(product, quantity);
  if (!item) return null;
  return pushEcommerceEvent(
    "view_item",
    buildEcommerce({
      currency: CURRENCY,
      value: toNumber(item.price) * toNumber(item.quantity),
      items: [item],
    }),
    {},
    { eventId: createEventId("view_item", item.item_id) }
  );
}

export function trackSearch(search_term) {
  const term = String(search_term || "").trim();
  if (!term) return null;
  return pushEvent(
    "search",
    { search_term: term, search_string: term, currency: CURRENCY },
    { eventId: createEventId("search") }
  );
}

export function trackAddToCart(productOrItem, quantity = 1) {
  const item =
    productOrItem?.item_id != null && productOrItem?.price != null && !productOrItem.skus
      ? pruneEmpty({
          ...productOrItem,
          item_id: String(productOrItem.item_id),
          price: toNumber(productOrItem.price),
          quantity: toNumber(quantity || productOrItem.quantity || 1) || 1,
        })
      : normalizeProduct(productOrItem, quantity);
  if (!item) return null;
  const qty = toNumber(item.quantity) || 1;
  return pushEcommerceEvent(
    "add_to_cart",
    buildEcommerce({
      currency: CURRENCY,
      value: toNumber(item.price) * qty,
      items: [{ ...item, quantity: qty }],
    }),
    {},
    { eventId: createEventId("add_to_cart") }
  );
}

export function trackRemoveFromCart(productOrItem, quantity = 1) {
  const item =
    productOrItem?.item_id != null && !productOrItem.skus
      ? pruneEmpty({
          ...productOrItem,
          item_id: String(productOrItem.item_id),
          price: toNumber(productOrItem.price),
          quantity: toNumber(quantity || productOrItem.quantity || 1) || 1,
        })
      : normalizeProduct(productOrItem, quantity);
  if (!item) return null;
  const qty = toNumber(item.quantity) || 1;
  return pushEcommerceEvent(
    "remove_from_cart",
    buildEcommerce({
      currency: CURRENCY,
      value: toNumber(item.price) * qty,
      items: [{ ...item, quantity: qty }],
    })
  );
}

export function trackViewCart(items, value) {
  const normalized = ensureItems(items);
  return pushEcommerceEvent(
    "view_cart",
    buildEcommerce({
      currency: CURRENCY,
      value: value != null ? toNumber(value) : cartItemsValue(normalized),
      items: normalized,
    }),
    {},
    { eventId: createEventId("view_cart") }
  );
}

export function trackBeginCheckout(items, value) {
  const normalized = ensureItems(items);
  return pushEcommerceEvent(
    "begin_checkout",
    buildEcommerce({
      currency: CURRENCY,
      value: value != null ? toNumber(value) : cartItemsValue(normalized),
      items: normalized,
    }),
    {},
    { eventId: createEventId("begin_checkout") }
  );
}

export function trackAddShippingInfo({ items, value, shipping_tier }) {
  const normalized = ensureItems(items);
  return pushEcommerceEvent(
    "add_shipping_info",
    buildEcommerce({
      currency: CURRENCY,
      value: value != null ? toNumber(value) : cartItemsValue(normalized),
      shipping_tier,
      items: normalized,
    }),
    pruneEmpty({ shipping_tier: shipping_tier || undefined })
  );
}

export function trackAddPaymentInfo({ items, value, payment_type }) {
  const normalized = ensureItems(items);
  return pushEcommerceEvent(
    "add_payment_info",
    buildEcommerce({
      currency: CURRENCY,
      value: value != null ? toNumber(value) : cartItemsValue(normalized),
      payment_type,
      items: normalized,
    }),
    pruneEmpty({ payment_type: payment_type || undefined }),
    { eventId: createEventId("add_payment_info") }
  );
}

function readStorageIds(storage, key) {
  if (!storage) return [];
  try {
    const raw = storage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch {
    return [];
  }
}

function writeStorageIds(storage, key, ids) {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify([...ids]));
  } catch {
    /* ignore quota / private mode */
  }
}

function readPurchaseIds() {
  const ids = new Set(purchaseIdsMemory);
  if (!canUseDom()) return ids;
  readStorageIds(window.sessionStorage, PURCHASE_STORAGE_KEY).forEach((id) =>
    ids.add(id)
  );
  readStorageIds(window.localStorage, PURCHASE_STORAGE_KEY_LS).forEach((id) =>
    ids.add(id)
  );
  return ids;
}

function rememberPurchaseId(id) {
  if (!id) return;
  const tid = String(id);
  purchaseIdsMemory.add(tid);
  if (!canUseDom()) return;
  const sessionIds = new Set([
    ...readStorageIds(window.sessionStorage, PURCHASE_STORAGE_KEY),
    tid,
  ]);
  writeStorageIds(window.sessionStorage, PURCHASE_STORAGE_KEY, sessionIds);
  const localIds = new Set([
    ...readStorageIds(window.localStorage, PURCHASE_STORAGE_KEY_LS),
    tid,
  ]);
  // Cap local history to avoid unbounded growth.
  writeStorageIds(
    window.localStorage,
    PURCHASE_STORAGE_KEY_LS,
    [...localIds].slice(-200)
  );
}

/**
 * purchase only once per transaction_id (memory + session + localStorage).
 * Requires real transaction_id, numeric value, and at least one item.
 * event_id = purchase_<order_number>
 */
export function trackPurchase({
  transaction_id,
  value,
  tax,
  shipping,
  coupon,
  payment_type,
  shipping_tier,
  items,
}) {
  const tid = transaction_id != null ? String(transaction_id).trim() : "";
  if (!tid) return false;
  if (readPurchaseIds().has(tid)) return false;

  const normalized = ensureItems(items);
  if (!normalized.length) return false;

  const numericValue =
    value != null ? toNumber(value) : cartItemsValue(normalized);
  if (!Number.isFinite(numericValue) || numericValue < 0) return false;

  const eventId = createEventId("purchase", tid);

  pushEcommerceEvent(
    "purchase",
    buildEcommerce({
      transaction_id: tid,
      currency: CURRENCY,
      value: numericValue,
      tax: tax != null ? toNumber(tax) : undefined,
      shipping: shipping != null ? toNumber(shipping) : undefined,
      coupon,
      payment_type,
      shipping_tier,
      items: normalized,
    }),
    {},
    { eventId }
  );
  rememberPurchaseId(tid);
  return true;
}

export function trackAddToWishlist(product, quantity = 1) {
  const item = normalizeProduct(product, quantity);
  if (!item) return null;
  return pushEcommerceEvent(
    "add_to_wishlist",
    buildEcommerce({
      currency: CURRENCY,
      value: toNumber(item.price) * toNumber(item.quantity),
      items: [item],
    }),
    {},
    { eventId: createEventId("add_to_wishlist", item.item_id) }
  );
}

export function trackLogin(method = "password") {
  return pushEvent("login", { method: String(method || "password") });
}

export function trackSignUp(method = "password") {
  return pushEvent(
    "sign_up",
    { method: String(method || "password"), meta_event: "CompleteRegistration" },
    { eventId: createEventId("sign_up") }
  );
}

/**
 * Quick order — not purchase. Stable event_id when order_number provided.
 */
export function trackQuickOrder({
  value,
  items,
  form_name = "quick_order",
  order_number,
  transaction_id,
}) {
  const normalized = ensureItems(items);
  const stable = order_number || transaction_id;
  return pushEcommerceEvent(
    "quick_order",
    buildEcommerce({
      currency: CURRENCY,
      value: value != null ? toNumber(value) : cartItemsValue(normalized),
      transaction_id: stable != null ? String(stable) : undefined,
      items: normalized,
    }),
    pruneEmpty({ form_name: form_name || "quick_order" }),
    { eventId: createEventId("quick_order", stable) }
  );
}

/**
 * Credit application — not purchase.
 */
export function trackCreditApplication({
  value,
  items,
  credit_term,
  form_name = "credit_application",
  order_number,
  transaction_id,
}) {
  const normalized = ensureItems(items);
  const stable = order_number || transaction_id;
  return pushEcommerceEvent(
    "credit_application",
    buildEcommerce({
      currency: CURRENCY,
      value: value != null ? toNumber(value) : cartItemsValue(normalized),
      transaction_id: stable != null ? String(stable) : undefined,
      items: normalized,
    }),
    pruneEmpty({
      form_name: form_name || "credit_application",
      credit_term: credit_term != null ? String(credit_term) : undefined,
    }),
    { eventId: createEventId("credit_application", stable) }
  );
}

export function trackGenerateLead({ lead_type, form_name }) {
  return pushEvent(
    "generate_lead",
    pruneEmpty({
      lead_type: lead_type || undefined,
      form_name: form_name || undefined,
      meta_event: "Lead",
    }),
    { eventId: createEventId("generate_lead") }
  );
}

export function trackPhoneClick() {
  return pushEvent(
    "phone_click",
    { meta_event: "Contact", contact_method: "phone" },
    { eventId: createEventId("phone_click") }
  );
}

export function trackWhatsappClick() {
  return pushEvent(
    "whatsapp_click",
    { meta_event: "Contact", contact_method: "whatsapp" },
    { eventId: createEventId("whatsapp_click") }
  );
}

export function trackViberClick() {
  return pushEvent(
    "viber_click",
    { meta_event: "Contact", contact_method: "viber" },
    { eventId: createEventId("viber_click") }
  );
}

export function trackSocialClick(social_network) {
  if (!social_network) return null;
  return pushEvent("social_click", {
    social_network: String(social_network),
  });
}

export function trackAddToCompare(product, quantity = 1) {
  const item = normalizeProduct(product, quantity);
  if (!item) return null;
  return pushEcommerceEvent(
    "add_to_compare",
    buildEcommerce({
      currency: CURRENCY,
      value: toNumber(item.price) * toNumber(item.quantity),
      items: [item],
    })
  );
}

export function trackAiProductOpen({ item_id, item_name }) {
  return pushEvent(
    "ai_product_open",
    pruneEmpty({
      item_id: item_id != null ? String(item_id) : undefined,
      item_name: item_name != null ? String(item_name) : undefined,
    })
  );
}

/**
 * SPA virtual pageview — call after route change completes.
 * Does not fire GA4 page_view (GTM/Google tag handles initial load).
 */
export function trackVirtualPageview({
  page_location,
  page_path,
  page_title,
} = {}) {
  if (!canUseDom()) return null;
  const path =
    page_path != null
      ? String(page_path)
      : `${window.location.pathname}${window.location.search}`;
  const loc =
    page_location != null ? String(page_location) : window.location.href;
  const title =
    page_title != null ? String(page_title) : document.title || "";
  return pushEvent("virtualPageview", {
    page_location: loc,
    page_path: path,
    page_title: title,
  });
}

/** Strip internal analytics field before API payloads. */
export function stripGaMeta(data) {
  if (!data || typeof data !== "object") return data;
  const { _ga, ...rest } = data;
  return rest;
}

export function getGaMeta(data) {
  return data && typeof data === "object" ? data._ga : null;
}
